import { observable, computed, action } from 'mobx';
import { App, ArrayApp, Model, ModelRegistry, AppRegistry } from 'core';
import Alert from 'react-s-alert';
import { SharedFileModel, SharedFileApp } from 'apps/share/app'; // getting these via ModelRegistry crashed the page.
// import { PermissionedModel } from 'apps/permissions/app';
import { FilesApp } from 'apps/file/app';

import { ManufacturerModel } from 'apps/manufacturing/app';
import { ObjToDBId } from 'apps/shipmentQuote/utils';
import { dgraphIdToDecimal } from 'utils/dgraphId';

export class MaterialModel extends Model {
  static registryName = 'MaterialModel';
  static ModelProps = {
    childMaterials: ['MaterialModel'],
    parentMaterial: 'MaterialModel',
  };

  @observable id;
  @observable name;
  @observable count = 0; // count: amount of selectable nodes below this node including this node.
  @observable isSelectable = false;
  @observable childMaterials = [];
  @observable parentMaterial = null;
  @observable shortDescription = null;
  @observable tags = null;

  @computed get isExpandable() {
    // Or just if app?.[nestedSelectables] return i.e. hasChildren (assuming the backend doesn't return [])
    if (this.childMaterials.length > 0) {
      return true;
    }
    return false;
  }
}
ModelRegistry.add(MaterialModel);

export class MaterialApp extends App {
  static registryName = 'MaterialApp';
  static Model = MaterialModel;
  static AppProps = {
    childMaterials: 'MaterialsApp',
    parentMaterial: 'MaterialApp',
  };

  categoryAppMode = false;

  @computed get tooltip() {
    if (this.tags === null) {
      this.tags = [];
    }

    return {
      shortDescription: this.shortDescription,
      tags: this.tags,
    };
  }

  @computed get isSelectableCategory() {
    return this.isSelectable && this.isExpandable;
  }

  @computed get getPath() {
    const segs = [];
    if (this.parentMaterial) {
      segs.push(...this.parentMaterial.getPath, this);
      return segs;
    }
    segs.push(this);
    return segs;
  }
}
AppRegistry.add(MaterialApp);

export class MaterialsApp extends ArrayApp {
  static registryName = 'MaterialsApp';
  static ElementApp = MaterialApp;
}
AppRegistry.add(MaterialsApp);

export class ProcessModel extends Model {
  static registryName = 'ProcessModel';
  static ModelProps = {
    childProcesses: ['ProcessModel'],
    parentProcess: 'ProcessModel',
  };

  @observable id;
  @observable name;
  @observable count = 0;
  @observable isSelectable = false;
  @observable childProcesses = [];
  @observable parentProcess = null;
  @observable shortDescription = null;
  @observable tags = null;

  @computed get isExpandable() {
    if (this.childProcesses.length > 0) {
      return true;
    }
    return false;
  }
}
ModelRegistry.add(ProcessModel);

export class ProcessApp extends App {
  static registryName = 'ProcessApp';
  static Model = ProcessModel;
  static AppProps = {
    childProcesses: 'ProcessesApp',
    parentProcess: 'ProcessApp',
  };

  categoryAppMode = false;

  @computed get tooltip() {
    if (this.tags === null) {
      this.tags = [];
    }

    return {
      shortDescription: this.shortDescription,
      tags: this.tags,
    };
  }

  @computed get isSelectableCategory() {
    return this.isSelectable && this.isExpandable;
  }

  @computed get getPath() {
    const segs = [];
    if (this.parentProcess) {
      segs.push(...this.parentProcess.getPath, this);
      return segs;
    }
    segs.push(this);
    return segs;
  }
}
AppRegistry.add(ProcessApp);

export function valueKey(optionModel) {
  return `value${optionModel.valueType.charAt(0)}${optionModel.valueType
    .slice(1)
    .toLowerCase()}`;
}
export class ProcessesApp extends ArrayApp {
  static registryName = 'ProcessesApp';
  static ElementApp = ProcessApp;
}
AppRegistry.add(ProcessesApp);

export class OptionModel extends Model {
  static registryName = 'OptionModel';
  static ModelProps = {
    values: ['OptionValueModel'],
    instances: ['OptionInstanceModel'],
  };

  @observable name = '';
  @observable valueType = 'INT'; // just a default
  @observable type = ''; // specifically for MinMaxOption which may be Min or Max;
  @observable instances = [];
  @observable values = [];

  @observable nilLabel = null;
  @observable required = true;
  @observable unit = null;
  @observable prefix = null;
  // 'for' may be a Material or Process (not currently supported in the framework)
  // but for now we can just set it as a value or create a new model for Material/Process
  @observable for = null;
  @computed get valueKey() {
    return valueKey(this);
  }
}
ModelRegistry.add(OptionModel);

/* We don't need Apps because nothing is going to be modified

export class OptionApp extends App {
  static registryName = 'OptionApp';
  static Model = OptionModel;
  static AppProps = {
    instances: 'OptionInstanceApp',
  };
}
AppRegistry.add(OptionApp); */

class OptionValBaseModel extends Model {
  static registryName = 'OptionValBaseModel';
  @observable valueInt = null;
  @observable valueFloat = null;
  @observable valueString = null;
  @observable valueBool = null;
  @observable option = null;
  @computed get text() {
    let prefix = '';
    let text;
    if (this.option.prefix) {
      ({ prefix } = this.option);
    }
    if (this.option.unit) {
      text = `${prefix}${this.value} ${this.option.unit}`;
    } else {
      text = `${prefix}${this.value}`;
    }
    return text;
  }

  @computed get value() {
    return this[this.option.valueKey];
  }
}

export class OptionInstanceModel extends OptionValBaseModel {
  static registryName = 'OptionInstanceModel';
  static ModelProps = {
    option: 'OptionModel',
    ForMaterial: 'MaterialModel',
    ForProcess: 'ProcessModel',
    // manufacturer: 'ManufacturerModel',
  };

  @observable manufacturer = null;
  @observable ForMaterial = null;
  @observable ForProcess = null;
}
ModelRegistry.add(OptionInstanceModel);

export class OptionValueModel extends OptionValBaseModel {
  static registryName = 'OptionValueModel';
  static ModelProps = {
    option: 'OptionModel',
  };

  @observable default = false;
}
ModelRegistry.add(OptionValueModel);

/* export class OptionInstanceApp extends App {
  static registryName = 'OptionInstanceApp';
  static Model = OptionInstanceModel;
  static AppProps = {
    option: 'OptionApp',
    ForMaterial: 'MaterialModel',
    ForProcess: 'MaterialProcess',
  };
}
AppRegistry.add(OptionInstanceApp);
*/

/*
Does not currently work because we don't allow Models with no IDs - T.S.

class BuildValueModel extends Model {
  static registryName = 'BuildValueModel';
  static ModelProps = {
    of: 'OptionModel',
  };

  @observable of = null;
  @observable valueFloat;
  @observable valueInt;
  @observable valueBool;
  @observable valueString;
}
ModelRegistry.add(BuildValueModel);
*/

export class BodyModel extends Model {
  static registryName = 'BodyModel';
  static ModelProps = {
    srcFile: 'FileModel',
  };

  @observable cadId = '';
  @observable error = null;
  @observable status = '';
  @observable srcFile = null;
}
ModelRegistry.add(BodyModel);

export class BodyApp extends App {
  static registryName = 'BodyApp';
  static Model = BodyModel;
  static AppProps = {
    srcFile: 'FileApp',
  };
}
AppRegistry.add(BodyApp);

export class BuildModel extends Model {
  static registryName = 'BuildModel';
  static ModelProps = {
    material: 'MaterialModel',
    process: 'ProcessModel',
    file: 'FileModel',
    supportingFiles: ['FileModel'],
    body: 'BodyModel',
    // values: [BuildValueModel],
  };

  @observable file = null;
  @observable material = null;
  @observable process = null;
  @observable values = [];
  @observable supportingFiles = [];
  @observable body = null;
}
ModelRegistry.add(BuildModel);

export class BuildApp extends App {
  static registryName = 'BuildApp';
  static Model = BuildModel;
  static AppProps = {
    material: 'MaterialApp',
    process: 'ProcessApp',
    file: 'FileApp',
    supportingFiles: 'FilesApp',
    body: 'BodyApp',
    // values: 'BuildValuesArrayApp',
  };

  @observable valuesTarget = {};
  @observable supportingFilesBeingAdded = [];

  @action SetSupportingFiles = newFiles => {
    this.supportingFiles = new FilesApp(null, newFiles);
  };

  @action ResetSupportingFiles = () => {
    this.supportingFiles = new FilesApp();
  };

  @action RemoveSupportingFileBeingAdded = suppFile => {
    const index = this.supportingFilesBeingAdded.findIndex(
      file => file.name === suppFile.filename,
    );
    if (index !== -1) this.supportingFilesBeingAdded.splice(index, 1);
  };
}
AppRegistry.add(BuildApp);

export class JobSpecModel extends Model {
  static registryName = 'JobSpecModel';
  static ModelProps = {
    build: 'BuildModel',
    quotes: ['ProductQuoteModel'],
  };

  @observable rfq = null;
  @observable customerSpaceId = null;
  // an input field cannot be set to null and we don't want a default integer
  // so we set quantity to an empty string (which is strange for an integer field)
  @observable quantity = '';
  @observable leadTime = '';
  @observable quotes = [];

  @observable build = null;
}
ModelRegistry.add(JobSpecModel);

export class JobSpecApp extends App {
  static registryName = 'JobSpecApp';
  static AppProps = {
    build: 'BuildApp',
    quotes: 'ProductQuotesArrayApp',
  };

  @computed get spaceId() {
    // possibly JobSpec should be independent of RFQ and get its space via the File
    // We should probably query spaceId onto rfq.
    // This works when creating a new RFQApp locally but won't work currently when loading an RFQ from the server
    return this.rfq.parent.spaceId;
  }

  static Model = JobSpecModel;
}
AppRegistry.add(JobSpecApp);
export class SessionModel extends Model {
  static registryName = 'SessionModel';
  static ModelProps = {
    shareFile: SharedFileModel,
  };

  @observable state = '';
  @observable createdAt = '';
  @observable shareFile = null;
}
ModelRegistry.add(SessionModel);

export class SessionApp extends App {
  static registryName = 'SessionApp';
  static AppProps = {
    shareFile: SharedFileApp,
  };

  static Model = SessionModel;
}
AppRegistry.add(SessionApp);

export class SessionArrayApp extends ArrayApp {
  static registryName = 'SessionArrayApp';
  static ElementApp = SessionApp;
}

export class FileRequirementModel extends Model {
  static registryName = 'FileRequirementModel';

  @observable id = null;
  @observable for = null; // : Requirable!
  @observable formats = []; // : [String!]!
  @observable type = null; // : RequirementType!
  @observable operator = null; // : Operator
  @observable valueInt = null;
  @observable valueFloat = null;
  @observable valueString = null;
  @observable valueBool = null;
}
ModelRegistry.add(FileRequirementModel);

export class FileRequirementApp extends App {
  static registryName = 'FileRequirementApp';
  static AppProps = {};

  static Model = FileRequirementModel;
}
AppRegistry.add(FileRequirementApp);

export class FileRequirementsArrayApp extends ArrayApp {
  static registryName = 'FileRequirementsArrayApp';
  static ElementApp = FileRequirementApp;
}
AppRegistry.add(FileRequirementsArrayApp);

export class RFQModel extends Model {
  static registryName = 'RFQModel';
  static ModelProps = {
    jobSpec: 'JobSpecModel',
    intermediatorSession: 'SessionModel',
    customerSpace: 'SpaceModel',
    fileRequirements: ['FileRequirementModel'],
    shipmentQuotes: ['ShipmentQuoteModel'],
    productQuotes: ['ProductQuoteModel'],
    // assignees: ['ManufacturerModel'],
  };

  @computed get dgraphId() {
    return ObjToDBId(this);
  }

  @computed get decimalId() {
    return dgraphIdToDecimal(this.dgraphId);
  }

  @observable state = null;
  @observable type = null;
  @observable quotesNeededBy = null;
  @observable estimatedAwardDate = null;
  @observable createdAt = null;
  @observable nextPossibleStates = null;
  @observable isQuotable = false;
  @observable isUpdatable = false;
  // @observable assignees = [];
  @observable jobSpec = null;
  @observable intermediatorSession = null;
  @observable customerSpace = null;
  @observable quotingPriorityData = null;
  @observable sessions = null;
  @observable address = null;
  @observable fileRequirements = [];
  @observable shipmentQuotes = [];
  @observable productQuotes = [];
}
ModelRegistry.add(RFQModel);

export class RFQApp extends App {
  static registryName = 'RFQApp';
  static Model = RFQModel;
  static AppProps = {
    jobSpec: 'JobSpecApp',
    intermediatorSession: 'SessionApp',
    sessions: SessionArrayApp,
    fileRequirements: 'FileRequirementsArrayApp',
    shipmentQuotes: 'ShipmentQuotesArrayApp',
    productQuotes: 'ProductQuotesArrayApp',
    // customerSpace?
  };

  @observable areFileRequirementsValidatedByServer = true;

  @action
  CopyLinkToClipboard = text => {
    // Create new element
    const el = document.createElement('textarea');
    // Set value (string to be copied)
    el.value = text;
    // Set non-editable to avoid focus and move outside of view
    el.setAttribute('readonly', '');
    el.style = { position: 'absolute', left: '-9999px' };
    document.body.appendChild(el);
    // Select text inside element
    el.select();
    // Copy text to clipboard
    // FIXME This will be deprecated https://developer.mozilla.org/en-US/docs/Web/API/Document/execCommand
    document.execCommand('copy');
    // Remove temporary element
    document.body.removeChild(el);
    Alert.success('Link copied to clipboard.');
  };
}
AppRegistry.add(RFQApp);

export class RFQArrayApp extends ArrayApp {
  static registryName = 'RFQArrayApp';
  static ElementApp = RFQApp;
}
AppRegistry.add(RFQArrayApp);
export class RFQAssigneesModel extends Model {
  static registryName = 'RFQAssigneesModel';
  /*
  // TODO: Change this definition once we have the implementation for the
  assignees query. For the moment, we can only assign the RFQ to one manufacturer
  static ModelProps = {
    assignees: [ManufacturerModel],
  };
  @observable assignees; */
  static ModelProps = {
    assignee: ManufacturerModel,
  };

  @observable rfqId;
  @observable assignee;
}
ModelRegistry.add(RFQAssigneesModel);
